import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { applyDomTranslations } from '../i18n/domTranslator';
import { LANGUAGE_STORAGE_KEY, languageCatalog, translateMessage } from '../i18n';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return languageCatalog[storedLanguage] ? storedLanguage : 'en';
  });

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const translate = useMemo(() => {
    return (key, params = {}) => translateMessage(language, key, params);
  }, [language]);

  useEffect(() => {
    const runTranslations = () => {
      applyDomTranslations(document.body, translate);
    };

    runTranslations();

    const observer = new MutationObserver(() => {
      runTranslations();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label']
    });

    return () => observer.disconnect();
  }, [translate]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    languages: Object.values(languageCatalog),
    t: translate
  }), [language, translate]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};
